import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, Pagination, Row, Table } from 'react-bootstrap';
import Select from 'react-select';
import { BsSearch } from 'react-icons/bs';
import { TableHeaderStyled } from 'pages/style/table/table-header';
import { Loading } from 'components/loading/loading-component';
import { getEnv } from 'helper/window-helper';
import { FluxoAprovacaoPedidoUseCase, OpcaoConsultaAprovador } from 'service/api/rest/fluxo-aprovacao-pedido-usecase';
import { SelectOptionInterface } from '../../../../@types/entity/interface/select-option-interface';
import {
  ConsultaAprovador,
  ConsultaAprovadorFiltro,
  EstadoAprovadorFiltro,
} from '../../../../@types/entity/consulta-aprovador-entity';

const FILTRO_VAZIO: ConsultaAprovadorFiltro = { estado: 'TODOS' };
const TAMANHO_PAGINA = 20;

export function FluxoAprovacaoPedidoConsultaAprovadores() {
  const endpoint = getEnv('REACT_APP_END_POINT_FLUXO_APROVACAO_PEDIDO')!;
  const service = useMemo(() => new FluxoAprovacaoPedidoUseCase(endpoint), [endpoint]);

  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(false);
  const [filiais, setFiliais] = useState<OpcaoConsultaAprovador[]>([]);
  const [aprovadoresDisponiveis, setAprovadoresDisponiveis] = useState<OpcaoConsultaAprovador[]>([]);
  const [resultado, setResultado] = useState<ConsultaAprovador[]>([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [pagina, setPagina] = useState(0);

  const [idsFilial, setIdsFilial] = useState<number[]>([]);
  const [idsAprovador, setIdsAprovador] = useState<number[]>([]);
  const [estado, setEstado] = useState<EstadoAprovadorFiltro>('TODOS');

  const opcoesFiliais = useMemo<SelectOptionInterface[]>(
    () => filiais.map((filial) => ({ value: filial.id, label: filial.nome })),
    [filiais]
  );
  const opcoesAprovadores = useMemo<SelectOptionInterface[]>(
    () => aprovadoresDisponiveis.map((aprovador) => ({ value: aprovador.id, label: aprovador.nome })),
    [aprovadoresDisponiveis]
  );

  const buscar = useCallback(
    async (filtro: ConsultaAprovadorFiltro, paginaAlvo: number) => {
      setCarregando(true);
      setErro(false);
      const resposta = await service.consultarAprovadores(filtro, paginaAlvo, TAMANHO_PAGINA);
      if (resposta.body === null) {
        setErro(true);
        setResultado([]);
        setTotalRegistros(0);
        setCarregando(false);
        return;
      }
      setResultado(resposta.body);
      setTotalRegistros(resposta.totalCount);
      setPagina(paginaAlvo);
      setCarregando(false);
    },
    [service]
  );

  useEffect(() => {
    service.listarFiliaisConsultaAprovadores().then((resposta) => setFiliais(resposta.body ?? []));
    service.listarAprovadoresConsulta().then((resposta) => setAprovadoresDisponiveis(resposta.body ?? []));
    buscar(FILTRO_VAZIO, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function montarFiltro(): ConsultaAprovadorFiltro {
    return {
      idFilial: idsFilial.length > 0 ? idsFilial : undefined,
      idAprovador: idsAprovador.length > 0 ? idsAprovador : undefined,
      estado,
    };
  }

  function limpar() {
    setIdsFilial([]);
    setIdsAprovador([]);
    setEstado('TODOS');
    buscar(FILTRO_VAZIO, 0);
  }

  function selecionados(opcoes: readonly SelectOptionInterface[] | null): number[] {
    return (opcoes ?? []).map((opcao) => Number(opcao.value));
  }

  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / TAMANHO_PAGINA));

  return (
    <Card className="mt-3" data-testid="consulta-aprovadores">
      <Card.Body>
        <Card.Subtitle className="text-muted mb-3">FILTROS DE PESQUISA</Card.Subtitle>
        <Row className="g-2 align-items-end">
          <Col md={4}>
            <Form.Group controlId="consultaAprovadoresFilial" data-testid="filtro-filial">
              <Form.Label>Filial</Form.Label>
              <Select
                inputId="consultaAprovadoresFilial"
                isMulti
                options={opcoesFiliais}
                value={opcoesFiliais.filter((opcao) => idsFilial.includes(opcao.value))}
                onChange={(opcoes) => setIdsFilial(selecionados(opcoes as SelectOptionInterface[]))}
                placeholder="Selecione o(s) valor(es)..."
                noOptionsMessage={() => 'Nenhuma filial encontrada'}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="consultaAprovadoresAprovador" data-testid="filtro-aprovador">
              <Form.Label>Aprovador</Form.Label>
              <Select
                inputId="consultaAprovadoresAprovador"
                isMulti
                options={opcoesAprovadores}
                value={opcoesAprovadores.filter((opcao) => idsAprovador.includes(opcao.value))}
                onChange={(opcoes) => setIdsAprovador(selecionados(opcoes as SelectOptionInterface[]))}
                placeholder="Selecione o(s) valor(es)..."
                noOptionsMessage={() => 'Nenhum aprovador encontrado'}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="consultaAprovadoresEstado">
              <Form.Label>Estado</Form.Label>
              <Form.Select value={estado} onChange={(e) => setEstado(e.target.value as EstadoAprovadorFiltro)}>
                <option value="TODOS">Todos</option>
                <option value="SEM_APROVADOR">SEM Aprovador</option>
                <option value="COM_APROVADOR">COM Aprovador</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col className="d-flex justify-content-end" style={{ gap: '8px' }}>
            <Button variant="secondary" onClick={limpar} disabled={carregando}>
              Limpar
            </Button>
            <Button variant="primary" onClick={() => buscar(montarFiltro(), 0)} disabled={carregando}>
              Filtrar <BsSearch />
            </Button>
          </Col>
        </Row>

        <Row style={{ marginTop: '10px', borderBottom: '1px solid #6C757D' }}></Row>

        {carregando ? (
          <Loading height="150px" />
        ) : erro ? (
          <div className="text-center py-4">
            <p className="text-muted">Não foi possível carregar a consulta de aprovadores.</p>
            <Button variant="outline-primary" size="sm" onClick={() => buscar(montarFiltro(), pagina)}>
              Tentar novamente
            </Button>
          </div>
        ) : (
          <>
            {renderTabela(resultado)}
            {resultado.length > 0 &&
              renderPaginacao(pagina, totalPaginas, carregando, (novaPagina) => buscar(montarFiltro(), novaPagina))}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

function renderTabela(linhas: ConsultaAprovador[]) {
  if (linhas.length === 0) {
    return <div className="text-muted mt-3">Nenhuma loja encontrada.</div>;
  }
  return (
    <TableHeaderStyled>
      <Table striped bordered hover responsive className="mt-3" data-testid="tabela-consulta-aprovadores">
        <thead>
          <tr>
            <th>Filial</th>
            <th>Tem Aprovador?</th>
            <th>Aprovador(es) Vinculado(s)</th>
          </tr>
        </thead>
        <tbody>{linhas.map(renderLinha)}</tbody>
      </Table>
    </TableHeaderStyled>
  );
}

function renderLinha(linha: ConsultaAprovador) {
  return (
    <tr key={linha.idFilial}>
      <td>{linha.nomeFilial}</td>
      <td>{linha.temAprovador ? <Badge bg="success">SIM</Badge> : <Badge bg="danger">NÃO</Badge>}</td>
      <td>
        {linha.temAprovador ? (
          linha.aprovadoresVinculados
        ) : (
          <span className="text-muted fst-italic">-- Nenhum aprovador vinculado --</span>
        )}
      </td>
    </tr>
  );
}

function renderPaginacao(
  paginaAtual: number,
  totalPaginas: number,
  desabilitado: boolean,
  irPara: (pagina: number) => void
) {
  if (totalPaginas <= 1) {
    return null;
  }
  return (
    <Pagination size="sm" className="mt-3 justify-content-end">
      <Pagination.First disabled={desabilitado || paginaAtual === 0} onClick={() => irPara(0)} />
      <Pagination.Prev disabled={desabilitado || paginaAtual === 0} onClick={() => irPara(paginaAtual - 1)} />
      <Pagination.Item disabled>
        {paginaAtual + 1} / {totalPaginas}
      </Pagination.Item>
      <Pagination.Next
        disabled={desabilitado || paginaAtual >= totalPaginas - 1}
        onClick={() => irPara(paginaAtual + 1)}
      />
      <Pagination.Last
        disabled={desabilitado || paginaAtual >= totalPaginas - 1}
        onClick={() => irPara(totalPaginas - 1)}
      />
    </Pagination>
  );
}

export default FluxoAprovacaoPedidoConsultaAprovadores;
